/**
 * Formatting that does not change with the machine it renders on.
 *
 * `toLocaleString()` and its date siblings, called with no locale, read the
 * *operating system's* locale — not the document's. The dashboard is served as
 * `<html lang="en">` and every sentence in it is written in English, so on a
 * Norwegian desktop the page rendered English prose carrying foreign
 * separators: "1 234 members", "31.7.2026". That is not a translation. It is
 * one document disagreeing with itself, in the one place a customer is most
 * likely to be counting — a member count they can go and check against Discord.
 *
 * It was also a test suite that passed in CI and failed on the author's own
 * laptop, which is the shape of bug that eats an afternoon and teaches people
 * to distrust a red suite.
 *
 * The time zone is deliberately NOT pinned. A timestamp should be shown on the
 * reader's own clock — that is a fact about them, and getting it "wrong" would
 * mean lying about when something happened. It is the separators, the digit
 * grouping and the month names that must follow the document's language rather
 * than the reader's keyboard layout.
 *
 * Every locale-sensitive rendering in this app goes through this module.
 * `format.test.ts` scans the source and fails if a bare `toLocale*()` call
 * reappears anywhere under `src/`.
 */

/**
 * The language the dashboard is actually written in, matching `<html lang="en">`
 * in index.html. If the product is ever translated, this becomes a lookup
 * against the active translation — not against `navigator.language`, which
 * describes the reader, not the text.
 */
const LOCALE = "en-US";

/**
 * A count, with thousands separators: 1234 → "1,234".
 *
 * Used for members, roles, channels and anything else a customer might compare
 * against a number Discord shows them.
 */
export function formatCount(value: number): string {
  return value.toLocaleString(LOCALE);
}

/** A date and a time of day, on the reader's clock: "7/31/2026, 6:57:00 PM". */
export function formatDateTime(value: Date): string {
  return value.toLocaleString(LOCALE);
}

/** A date alone, no time of day: "7/31/2026". */
export function formatDateNumeric(value: Date): string {
  return value.toLocaleDateString(LOCALE);
}

/** A calmer date for documents people read rather than scan: "Jul 31, 2026". */
export function formatDateLong(value: Date): string {
  return value.toLocaleDateString(LOCALE, { year: "numeric", month: "short", day: "numeric" });
}
